import { useEffect, useState } from 'react';
import { loadAttemptViews, loadRevealViews } from '../data/repository';
import { report, отпустить } from '../sentry';
import type { AttemptView, RevealView } from '../engine/mastery';

export interface Journal {
  attempts: AttemptView[];
  reveals: RevealView[];
}

export type JournalState =
  | { status: 'loading' }
  | { status: 'ready'; journal: Journal }
  | { status: 'failed' };

/** Журнал одного профиля для экранов прогресса и примера. Читается один раз
 *  при открытии экрана: оба показывают срез на момент входа, живого
 *  обновления им не нужно.

 *
 *  Отказ чтения — отдельное состояние, а не молчание. Без него отклонённый
 *  промис оставлял бы экран в «Загрузка…» навсегда, и уйти было бы нечем:
 *  IndexedDB отказывает в приватном окне, в webview внутри мессенджера и при
 *  откате выкладки на меньшую версию схемы. */
export function useJournal(profileId: string): JournalState {
  const [state, setState] = useState<JournalState>({ status: 'loading' });

  useEffect(() => {
    let alive = true;
    /** Вся работа — внутри try/catch ниже, и он сам ловит и сообщает любой
     *  отказ (`report`, строка ниже): обещание этой немедленно вызванной
     *  функции никогда не отклоняется. `отпустить` без места — только явная
     *  пометка намерения, а не повторное сообщение. */
    отпустить((async () => {
      try {
        const [attempts, reveals] = await Promise.all([
          loadAttemptViews(profileId),
          loadRevealViews(profileId),
        ]);
        // Экран мог закрыться, пока читали: обновлять снятый компонент нельзя.
        if (alive) setState({ status: 'ready', journal: { attempts, reveals } });
      } catch (error) {
        report(error, 'loadJournal');
        if (alive) setState({ status: 'failed' });
      }
    })());
    return () => {
      alive = false;
    };
  }, [profileId]);

  return state;
}
